import { useState } from 'react';
import type { CardMenuItem } from '@tempmee/core';
import { InviteProfessionalPopup } from '~/commonComponents/InviteProfessional/Popup';
import { CancelShiftConfirmPopup } from '~/components/shared/CancelShiftConfirmPopup';
import { useCancelJobMutation } from '~/domain/Jobs';
import { IJob } from '~/models';
import { getStandardCancelFlow } from '~/services/posthog/FeatureFlagUtils';

/** PostHog source label for invites sent from a shift-details page. */
const INVITE_SOURCE = 'Shift Details';

interface IUseShiftActionsMenuArgs {
  /**
   * The shift's id from the route, NOT `job.id`.
   *
   * The menu lives in the page header, which is up before the job query
   * resolves — so an office can open it and pick Cancel while `job` is still
   * undefined. Reading the id off the job would cancel `undefined`.
   */
  shiftId?: string;
  job: Partial<IJob> | null | undefined;
}

/**
 * The page header's three-dot menu: Invite and Cancel, both wired to the flows
 * that already exist.
 *
 * Edit is not offered: a shift cannot be edited once a professional has
 * applied, which is the rule the Confirm Candidate list card already applies
 * (GOTU-14996).
 *
 * Neither action is new behaviour — Invite is the shared
 * `InviteProfessionalPopup` and Cancel is the list card's
 * `CancelShiftConfirmPopup`, leaving for the cancellation-feedback screen like
 * every other shift-details page. `useConfirmExitNavigation` lets that redirect
 * through for this shift, so nothing here touches the navigation guard. What
 * this owns is the popups' open state, which is why the popup elements come back
 * with the items rather than being left for the page to remember to render.
 *
 * The cancel popup closes as soon as it is confirmed, so `isCancelPending` is
 * returned for the page to hold off Confirm / Decline while the cancel is in
 * flight — otherwise an office could book a candidate onto a shift that is
 * being cancelled.
 */
export const useShiftActionsMenu = ({ shiftId, job }: IUseShiftActionsMenuArgs) => {
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const { mutate: cancelJob, isPending: isCancelPending } = useCancelJobMutation();

  const items: CardMenuItem[] = [
    {
      label: 'Invite Professionals',
      onSelect: () => setIsInviteOpen(true),
      testID: 'shift-details-invite',
    },
    {
      label: 'Cancel Shift',
      onSelect: () => {
        // Records the A/B exposure; the popup picks its variant with a render-time peek.
        getStandardCancelFlow();
        setIsCancelOpen(true);
      },
      testID: 'shift-details-cancel-action',
    },
  ];

  const invitePopup = isInviteOpen ? (
    <InviteProfessionalPopup
      isOpen
      isRefetchJob
      jobId={shiftId}
      profession={job?.profession}
      officeId={job?.offer_owner?.id}
      trackingSource={INVITE_SOURCE}
      onClose={() => setIsInviteOpen(false)}
    />
  ) : null;

  // `isInterview` is always false: an onsite interview with applicants is
  // delegated to the legacy screen before this page renders.
  const cancelPopup =
    isCancelOpen && shiftId ? (
      <CancelShiftConfirmPopup
        isInterview={false}
        date={job?.local_date}
        isPending={isCancelPending}
        onClose={() => setIsCancelOpen(false)}
        onConfirm={() => {
          setIsCancelOpen(false);
          cancelJob({ jobId: shiftId, redirect: true });
        }}
      />
    ) : null;

  return { items, invitePopup, cancelPopup, isCancelPending };
};
